import { useMutation, useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

// Cost ledger summary for the current tenant per anchor §3.7.
// Backend endpoint lands in PR-07 (#144) — until then the route 404s
// against the dev server and tests stub fetch.

export interface CostLineItem {
  category: string; // e.g. "compute", "gpu_minutes", "ai_tokens", "storage"
  current_month_usd: number;
  total_usd: number;
}

export interface TileSpend {
  tile_name: string;
  current_month_usd: number;
  total_usd: number;
  run_count: number;
}

// The org's wallet (C1.5, U2.1). Money is an exact decimal string
// ("12.500000"), never a float on the wire.
export interface WalletFigures {
  balance_usd: string;
  held_usd: string;
  available_usd: string;
  currency: string;
}

// The org's spending limits (C1.6, U2.3); null is no limit.
export interface SpendingLimits {
  monthly_cap_usd: string | null;
  per_run_cap_usd: string | null;
  max_concurrent_runs: number | null;
  charged_this_month_usd: string;
  remaining_cap_usd: string | null;
}

export interface BudgetSummary {
  tenant_id: string;
  current_month_usd: number;
  total_usd: number;
  // The org's monthly cap (C1.6), enforced at Tile submission. Null when
  // there is no cap: the GUI renders "No cap", never a zero.
  monthly_budget_usd: string | null;
  // Breakdown by category — sums to current_month_usd.
  line_items: CostLineItem[];
  // Top-N tiles by current-month spend.
  top_tiles: TileSpend[];
  // Period the current_month_usd covers (ISO YYYY-MM-DD ranges).
  period_start: string;
  period_end: string;
  // Which pricing table produced the dollar figures (U0.3, C0.5).
  // "placeholder-estimate" = the Phase-0 placeholder table — the GUI
  // renders an "estimate" badge for exactly that value. Absent on
  // older servers → no badge.
  pricing_basis?: string;
  // Absent on servers older than U2.1 / U2.3.
  wallet?: WalletFigures;
  limits?: SpendingLimits;
}

export const BUDGET_QUERY_KEY = ["budget", "summary"] as const;

export function useBudget() {
  return useQuery({
    queryKey: BUDGET_QUERY_KEY,
    queryFn: async (): Promise<BudgetSummary> => apiFetch<BudgetSummary>("/api/budget"),
    staleTime: 60_000,
  });
}

// A Stripe Checkout deposit (U4.1, fs#1218). The server prices the Checkout
// page; the wallet is credited only by Stripe's signed webhook, never here.
export interface DepositStarted {
  checkout_url: string;
  checkout_session_id: string;
  amount_usd: string;
  currency: string;
}

/** `POST /api/billing/deposit` with a decimal-string amount in whole cents. */
export function useStartDeposit() {
  return useMutation({
    mutationFn: (amountUsd: string) =>
      apiFetch<DepositStarted>("/api/billing/deposit", {
        method: "POST",
        json: { amount_usd: amountUsd },
      }),
  });
}
